// «Чаты»: направления, в рабочих чатах которых работает робот OMA. По каждому направлению — его чаты (роль, режим, решения),
// анализ, который пишет сам робот по всем чатам направления, и готовность команды агентов работать самостоятельно:
// общая и по видам работы, что для этого нужно, как менялась оценка. Теневой режим: агент разбирает каждое «взял бы в работу»
// только на чтение, через сутки его итог сверяется с работой людей — счёт за 7 дней, расход и потолок.
import { useEffect, useId, useRef, useState } from 'react'
import { api } from '../api.ts'
import { Icon } from '../components/Icon.tsx'
import { Badge, Button, Empty, ErrorBox, Field, Loading, PageHeader, Section, Tabs, Toggle, cx } from '../components/ui.tsx'
import { useAsync, useBusy, useDebounced, useDebouncedValue } from '../lib/hooks.ts'
import { formatDateTime, formatUsd, timeAgo } from '../lib/format.ts'
import { Markdown } from '../lib/markdown.tsx'
import { Link, TaskLink, setSearchParams, useSearchParam } from '../router.tsx'
import { refreshCatalog, useLive, useNow, useReconnect, useStream } from '../store.ts'
import { loadPref, savePref } from '../theme.ts'
import { toast, toastError } from '../toast.ts'
import { SHADOW_OUTCOME_LABELS } from '../types.ts'
import type { ChatCandidate, ChatReport, ChatReportArea, ChatSearch, ConnectResult, ShadowOutcome, ShadowScore, Tone, WorkChatGroup, WorkChatMode } from '../types.ts'

const MODE: Record<WorkChatMode, { label: string; tone: Tone; hint: string }> = {
  off: { label: 'Выключен', tone: 'neutral', hint: 'робот чат не читает' },
  observe: { label: 'Наблюдение', tone: 'accent', hint: 'читает и разбирает, в чат не пишет и задач не ставит' },
  work: { label: 'Работа', tone: 'ok', hint: 'сам берёт задачи и пишет в чат' },
}

const AREA: Record<ChatReportArea['readiness'], { label: string; tone: Tone }> = {
  ready: { label: 'готов', tone: 'ok' },
  partly: { label: 'частично', tone: 'warn' },
  not_ready: { label: 'не готов', tone: 'danger' },
}

const VERDICTS: { key: string; label: string }[] = [
  { key: 'task', label: 'взял бы в работу' },
  { key: 'followup', label: 'продолжение задачи' },
  { key: 'answer', label: 'ответил бы' },
  { key: 'skip', label: 'не наше' },
]

const toneOf = (r: number): Tone => (r >= 80 ? 'ok' : r >= 50 ? 'accent' : r >= 25 ? 'warn' : 'danger')
const barColor: Record<Tone, string> = { ok: 'bg-ok', accent: 'bg-accent', warn: 'bg-warn', danger: 'bg-danger', neutral: 'bg-subtle' }
const textColor: Record<Tone, string> = { ok: 'text-ok', accent: 'text-accent', warn: 'text-warn', danger: 'text-danger', neutral: 'text-muted' }

/** Готовность: крупная цифра, уровень и шкала с делениями уровней. */
function Readiness({ value, level }: { value: number; level: string }) {
  const tone = toneOf(value)
  return (
    <div className="flex min-w-[220px] flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <span className={cx('tnum text-[40px] leading-none font-semibold', textColor[tone])}>{value}%</span>
        <span className="text-[13px] text-muted" title="Какую часть работы разработчиков направления агенты сделают сами; бизнес-решения в счёт не идут">готовность · {level}</span>
      </div>
      <div className="relative h-2 overflow-hidden rounded-full bg-surface-3" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={value} aria-label="Готовность к самостоятельной работе">
        <div className={cx('h-full rounded-full', barColor[tone])} style={{ width: `${Math.max(2, value)}%` }} />
        {[25, 50, 80].map((x) => (
          <span key={x} aria-hidden="true" className="absolute top-0 h-full w-px bg-bg" style={{ left: `${x}%` }} />
        ))}
      </div>
      <div className="flex justify-between text-[10.5px] text-subtle">
        <span>учусь</span>
        <span>частично</span>
        <span>большая часть</span>
        <span>готов</span>
      </div>
    </div>
  )
}

/** Как менялась оценка: столбики по отчётам. */
function History({ points }: { points: WorkChatGroup['history'] }) {
  if (points.length < 2) return null
  const first = points[0]!
  const last = points.at(-1)!
  return (
    <div className="min-w-[180px]">
      <div className="mb-1.5 text-[12px] font-medium text-muted">
        Как менялась готовность: <span className="tnum text-fg">{first.readiness}% → {last.readiness}%</span>
      </div>
      <div className="flex h-14 items-end gap-1 border-b border-line">
        {points.map((p) => (
          <div key={p.created_at} className="flex h-full w-5 items-end" title={`${formatDateTime(p.created_at)} — ${p.readiness}%`}>
            <div className={cx('w-full rounded-t-sm', barColor[toneOf(p.readiness)])} style={{ height: `${Math.max(4, p.readiness)}%` }} />
          </div>
        ))}
      </div>
    </div>
  )
}

function Areas({ areas }: { areas: ChatReportArea[] }) {
  const sorted = [...areas].sort((a, b) => b.share - a.share)
  return (
    // на телефоне — фиксированная раскладка: узкие «Доля» и «Робот», первая колонка забирает остаток и переносит длинные слова
    <div className="overflow-hidden rounded-card border border-line max-md:overflow-x-auto">
      <table className="w-full text-[13px] max-md:table-fixed">
        <thead className="bg-surface-2 text-left text-[12px] text-muted">
          <tr>
            <th className="px-3 py-2 font-medium max-md:px-2.5">Вид работы</th>
            <th className="w-[70px] px-2 py-2 text-right font-medium max-md:w-[56px]">Доля</th>
            <th className="w-[100px] px-2 py-2 font-medium max-md:w-[92px]">Робот</th>
            <th className="hidden px-3 py-2 font-medium md:table-cell">Почему · что нужно</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {sorted.map((a) => (
            <tr key={a.name} className="align-top">
              <td className="px-3 py-2.5 max-md:px-2.5 max-md:[overflow-wrap:anywhere]">
                <div className="font-medium text-fg">{a.name}</div>
                <div className="mt-1 text-[12.5px] text-fg-2 md:hidden">{a.note}</div>
                {a.needs ? <div className="mt-1 text-[12px] text-muted md:hidden">Нужно: {a.needs}</div> : null}
              </td>
              <td className="tnum px-2 py-2.5 text-right text-fg-2">{a.share}%</td>
              <td className="px-2 py-2.5">
                <Badge tone={AREA[a.readiness].tone}>{AREA[a.readiness].label}</Badge>
                {a.pct != null ? <div className="tnum mt-1 text-[11.5px] text-muted" title="Какую часть этой работы агенты сделают сами">сами {a.pct}%</div> : null}
              </td>
              <td className="hidden px-3 py-2.5 [overflow-wrap:anywhere] md:table-cell">
                <div className="text-fg-2">{a.note}</div>
                {a.needs ? <div className="mt-1 text-[12px] text-muted">Нужно: {a.needs}</div> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const QUESTIONS: { key: 'agents_take' | 'people_keep' | 'time_lost' | 'needs'; title: string; tone: Tone }[] = [
  { key: 'agents_take', title: 'Что агенты возьмут сами', tone: 'ok' },
  { key: 'people_keep', title: 'Что остаётся людям', tone: 'neutral' },
  { key: 'time_lost', title: 'Где теряется время сейчас', tone: 'warn' },
  { key: 'needs', title: 'Чего не хватает агентам, чтобы взять больше', tone: 'accent' },
]

/** Четыре вопроса владельца по направлению. В старых анализах были только «что нужно» — показываем то, что есть. */
function Questions({ report }: { report: ChatReport }) {
  const blocks = QUESTIONS.map((q) => ({ ...q, items: report[q.key] ?? [] })).filter((q) => q.items.length)
  if (!blocks.length) return null
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {blocks.map((q) => (
        <div key={q.key} className="min-w-0 rounded-card border border-line bg-surface-2 px-4 py-3">
          <div className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-fg">
            <span aria-hidden="true" className={cx('h-2 w-2 shrink-0 rounded-full', barColor[q.tone])} />
            {q.title}
          </div>
          <ul className="list-disc space-y-1 pl-5 text-[13.5px] break-words text-fg-2 marker:text-subtle">
            {q.items.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

const verdictCount = (v: Record<string, number>): number => VERDICTS.reduce((n, x) => n + (v[x.key] ?? 0), 0)

/** Чаты направления: роль каждого (из анализа), режим, решения разбора за неделю, сегодняшние цифры. */
function ChatsList({ g, report }: { g: WorkChatGroup; report: ChatReport | null }) {
  return (
    <div className="flex flex-col divide-y divide-line rounded-card border border-line">
      {g.chats.map(({ chat, chat_url, verdicts_7d }) => {
        const role = report?.chats.find((c) => c.dialog_id === chat.dialog_id)
        const st = chat.stats
        const total = verdictCount(verdicts_7d)
        return (
          <div key={chat.dialog_id} className="flex flex-col gap-1.5 px-3.5 py-3">
            <div className="flex flex-wrap items-center gap-2">
              <Icon name="message" size={14} className="shrink-0 text-subtle" />
              <span className="min-w-0 text-[14px] font-semibold [overflow-wrap:anywhere] text-fg">{chat.title || chat.dialog_id}</span>
              <Badge tone={MODE[chat.mode].tone} title={MODE[chat.mode].hint}>
                {MODE[chat.mode].label}
              </Badge>
              {role && g.chats.length > 1 ? <span className="tnum text-[12.5px] text-muted">{role.share}% работы направления</span> : null}
              {chat.mode !== 'off' ? (
                chat.bot_member_at ? (
                  <span className="inline-flex items-center gap-1 text-[12px] text-ok" title="Бот OMA — участник чата">
                    <Icon name="bot" size={13} />
                    бот в чате
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[12px] text-warn" title="Писать в чат бот сможет, только когда станет его участником">
                    <Icon name="alert" size={13} />
                    бота нет в чате
                  </span>
                )
              ) : null}
              <span className="ml-auto" />
              <a href={chat_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12.5px] text-muted hover:text-fg hover:underline">
                <Icon name="external" size={13} />
                в Битрикс24
              </a>
            </div>
            {role?.role ? (
              <p className="text-[13px] break-words text-fg-2">
                {role.role}
                {role.note ? <span className="text-muted"> · {role.note}</span> : null}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-subtle">
              {total ? (
                <span>
                  за 7 дней:{' '}
                  {VERDICTS.filter((v) => verdicts_7d[v.key]).map((v, i) => (
                    <span key={v.key}>
                      {i ? ', ' : ''}
                      <span className="tnum font-medium text-fg-2">{verdicts_7d[v.key]}</span> {v.label}
                    </span>
                  ))}
                </span>
              ) : (
                <span>новых сообщений после подключения пока не было</span>
              )}
              {st ? <span>сегодня: сообщений {st.messages_today}, ждут разбора {st.pending}</span> : null}
              {chat.triage_task_id ? (
                <TaskLink id={chat.triage_task_id} className="hover:underline">
                  разбор за сегодня
                </TaskLink>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const SHADOW_TONE: Record<ShadowOutcome, Tone> = { match: 'ok', agent_better: 'ok', human_better: 'warn', human_none: 'accent', agent_wrong: 'danger', unclear: 'neutral' }
const SHADOW_ORDER: ShadowOutcome[] = ['match', 'agent_better', 'human_better', 'human_none', 'agent_wrong', 'unclear']
const minutes = (m: number | null): string => (m == null ? '—' : m < 60 ? `${m} мин` : `${Math.floor(m / 60)} ч ${m % 60 ? `${m % 60} мин` : ''}`.trim())

/** Теневой режим направления: счёт сверок за 7 дней, время до ответа, расход и потолок, последние случаи. */
function Shadow({ g, onChanged }: { g: WorkChatGroup; onChanged: () => void }) {
  const sc: ShadowScore | undefined = g.shadow
  const [busy, run] = useBusy()
  const [budget, setBudget] = useState('')
  const budgetId = useId()
  if (!sc) return null
  const observing = g.chats.some((c) => c.chat.mode === 'observe')
  const save = (patch: { enabled?: boolean; budget_usd?: number }) =>
    run(async () => {
      try {
        await api.put(`/api/work-chats/groups/${encodeURIComponent(g.key)}/shadow`, patch)
        toast(patch.enabled === undefined ? 'Потолок тени сохранён' : patch.enabled ? 'Теневой режим включён' : 'Теневой режим выключен', 'success')
        setBudget('')
        onChanged()
      } catch (e) {
        toastError(e, 'Не сохранено')
      }
    })
  const value = budget === '' ? String(sc.budget_usd) : budget
  const parsed = Number(value.replace(',', '.'))
  const over = sc.cost_today_usd >= sc.budget_usd || sc.total_cost_today_usd >= sc.total_budget_usd
  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-surface-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-semibold text-fg">Теневой режим (7 дней)</span>
        <Badge tone={sc.enabled ? 'accent' : 'neutral'}>{sc.enabled ? 'включён' : 'выключен'}</Badge>
        {sc.enabled && over ? <Badge tone="warn">потолок на сегодня исчерпан</Badge> : null}
      </div>
      <p className="text-[12.5px] break-words text-muted">
        {observing
          ? 'По каждому «взял бы в работу» агент направления разбирает случай только на чтение — в чат не пишет. Через сутки робот сверяет его итог с тем, что сделали люди.'
          : 'Тень работает только для чатов в наблюдении: в «работе» робот ставит настоящие задачи, а несверенные тени чата снимаются без сверки.'}
      </p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
        <span>
          <span className="tnum font-semibold text-fg">{sc.analyzed_7d}</span> <span className="text-muted">разобрано агентом</span>
        </span>
        <span>
          <span className="tnum font-semibold text-fg">{sc.compared_7d}</span> <span className="text-muted">сверено с людьми</span>
        </span>
      </div>
      {sc.compared_7d ? (
        <div className="flex flex-wrap gap-1.5">
          {SHADOW_ORDER.map((o) => (
            <Badge key={o} tone={sc.outcomes_7d[o] ? SHADOW_TONE[o] : 'neutral'}>
              <span className="tnum">{sc.outcomes_7d[o] ?? 0}</span>&nbsp;{SHADOW_OUTCOME_LABELS[o]}
            </Badge>
          ))}
        </div>
      ) : null}
      <div className="text-[13px] text-fg-2">
        Медиана до ответа: агент — <span className="tnum font-medium text-fg">{minutes(sc.agent_minutes_median)}</span>, люди —{' '}
        <span className="tnum font-medium text-fg">{minutes(sc.human_minutes_median)}</span>
      </div>
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <Toggle checked={sc.enabled} disabled={busy} onChange={(v) => void save({ enabled: v })} label="Теневые разборы" description="вкл/выкл для направления" />
        <Field label="Потолок в день, $" htmlFor={budgetId} className="w-[150px]">
          <input
            id={budgetId}
            className="ctl tnum"
            inputMode="decimal"
            value={value}
            onChange={(e) => setBudget(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && Number.isFinite(parsed) && parsed >= 0) void save({ budget_usd: parsed })
            }}
          />
        </Field>
        {budget !== '' && budget !== String(sc.budget_usd) ? (
          <Button size="sm" variant="secondary" loading={busy} disabled={!Number.isFinite(parsed) || parsed < 0} onClick={() => void save({ budget_usd: parsed })}>
            Сохранить
          </Button>
        ) : null}
        <div className="text-[12px] text-subtle">
          Расход тени сегодня <span className="tnum">{formatUsd(sc.cost_today_usd)}</span> из <span className="tnum">{formatUsd(sc.budget_usd)}</span> · все направления{' '}
          <span className="tnum">{formatUsd(sc.total_cost_today_usd)}</span> из <span className="tnum">{formatUsd(sc.total_budget_usd)}</span>
        </div>
      </div>
      {sc.recent.length ? (
        <ul className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface">
          {sc.recent.map((c) => (
            <li key={c.id} className="flex flex-col gap-1 px-3 py-2 text-[13px]">
              <div className="flex flex-wrap items-center gap-2">
                <TaskLink id={c.task_id} className="font-mono text-[12px] text-muted hover:underline">
                  {c.task_key}
                </TaskLink>
                {c.outcome ? (
                  <Badge tone={SHADOW_TONE[c.outcome]}>{SHADOW_OUTCOME_LABELS[c.outcome]}</Badge>
                ) : c.compared_at ? (
                  <Badge tone="neutral">без сверки</Badge>
                ) : (
                  <Badge tone="neutral">{c.task_status === 'done' ? 'ждёт сверки' : 'агент разбирает'}</Badge>
                )}
                <span className="text-[12px] text-subtle">{formatDateTime(c.created_at)}</span>
                {c.agent_minutes != null || c.human_minutes != null ? (
                  <span className="tnum text-[12px] text-subtle">
                    агент {minutes(c.agent_minutes)} · люди {minutes(c.human_minutes)}
                  </span>
                ) : null}
              </div>
              <div className="break-words text-fg-2">{c.message}</div>
              {c.note ? <div className="text-[12.5px] break-words text-muted">{c.note}</div> : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[13px] text-subtle">Теневых разборов пока не было.</div>
      )}
    </div>
  )
}

const chatsWord = (n: number): string => (n % 10 === 1 && n % 100 !== 11 ? 'чат' : [2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100) ? 'чата' : 'чатов')

function GroupCard({ g, onRequested }: { g: WorkChatGroup; onRequested: () => void }) {
  const now = useNow()
  const [busy, run] = useBusy()
  const [adding, setAdding] = useState(false)
  const report = g.report
  const multi = g.chats.length > 1
  const total = verdictCount(g.verdicts_7d)
  const ask = () =>
    run(async () => {
      try {
        await api.post(`/api/work-chats/groups/${encodeURIComponent(g.key)}/report`, {})
        toast('Робот напишет свежий анализ в ближайшие минуты', 'success')
        onRequested()
      } catch (e) {
        toastError(e, 'Не получилось попросить анализ')
      }
    })
  const buttons = (
    <>
      {g.direction_id ? (
        <Button size="sm" variant="ghost" icon="plus" onClick={() => setAdding((v) => !v)}>
          Добавить чат
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" icon="refresh" loading={busy} disabled={g.report_pending} onClick={() => void ask()}>
        {g.report_pending ? 'Анализ пишется…' : 'Обновить анализ'}
      </Button>
    </>
  )
  return (
    <Section
      title={
        <span className="inline-flex min-w-0 flex-wrap items-center gap-2">
          <Icon name={g.direction_id ? 'graph' : 'message'} size={16} className="shrink-0 text-subtle" />
          <span className="min-w-0 [overflow-wrap:anywhere]">{g.direction_name ? `Направление «${g.direction_name}»` : g.chats[0]?.chat.title || g.key}</span>
          <span className="text-[13px] font-normal normal-case text-muted">
            · {g.chats.length} {chatsWord(g.chats.length)}
          </span>
        </span>
      }
      // на телефоне кнопки — отдельной строкой в карточке: в шапке рядом с названием им тесно
      actions={<div className="flex flex-wrap gap-1.5 max-md:hidden">{buttons}</div>}
    >
      <div className="flex flex-col gap-5">
        <div className="-mt-1 -ml-2.5 flex flex-wrap gap-1.5 md:hidden">{buttons}</div>
        {adding && g.direction_id ? <ConnectPanel presetDirection={g.direction_id} onDone={onRequested} onClose={() => setAdding(false)} /> : null}
        {report ? (
          <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-[16px] leading-snug font-semibold break-words text-fg">{report.headline}</p>
              <p className="mt-1.5 text-[12px] break-words text-subtle" title={formatDateTime(report.created_at)}>
                Анализ робота{multi ? ' по всем чатам направления' : ''} · {timeAgo(report.created_at, now)}
                {report.basis ? ` · ${report.basis}` : ''}
              </p>
            </div>
            <Readiness value={report.readiness} level={report.level} />
          </div>
        ) : (
          <Empty icon="bot">
            <div className="font-medium text-fg">{g.report_pending ? 'Робот пишет первый анализ' : 'Анализа пока нет'}</div>
            <div className="mt-1 text-[13px]">
              {g.report_pending
                ? 'Обычно это занимает несколько минут — страница обновится сама.'
                : 'Нажмите «Обновить анализ» — робот изучит переписку и оценит, насколько команда агентов готова работать сама.'}
            </div>
          </Empty>
        )}
        {report ? <Questions report={report} /> : null}
        <div>
          <div className="mb-1.5 text-[13px] font-semibold text-fg">{multi ? 'Чаты направления' : 'Чат'}</div>
          <ChatsList g={g} report={report} />
        </div>
        {report ? (
          <>
            <div>
              <div className="mb-1.5 text-[13px] font-semibold text-fg">Виды работы: доля и сколько агенты сделают сами</div>
              <Areas areas={report.areas} />
            </div>
            <details className="group rounded-card border border-line bg-surface-2 px-4 py-3" open>
              <summary className="cursor-pointer text-[13px] font-semibold text-fg">{multi ? 'Анализ направления' : 'Анализ чата'}</summary>
              <Markdown text={report.analysis} className="mt-2 text-[13.5px] leading-relaxed text-fg-2" />
            </details>
          </>
        ) : null}
        <Shadow g={g} onChanged={onRequested} />
        <div className="grid grid-cols-1 gap-4 border-t border-line pt-4 md:grid-cols-[1fr_auto]">
          <div>
            <div className="mb-1.5 text-[12px] font-medium text-muted">
              Решения робота по сообщениям за 7 дней{multi ? ' (все чаты)' : ''}
              {total ? ` · ${total}` : ''}
            </div>
            {total ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px]">
                {VERDICTS.map((v) => (
                  <span key={v.key}>
                    <span className="tnum font-semibold text-fg">{g.verdicts_7d[v.key] ?? 0}</span> <span className="text-muted">{v.label}</span>
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-[13px] text-subtle">Новых сообщений после подключения пока не было.</div>
            )}
            <div className="mt-2 text-[12px] text-subtle">
              Потрачено сегодня {formatUsd(g.cost_today_usd)} из {formatUsd(g.budget_usd)} ·{' '}
              <Link to="/settings" className="hover:underline">
                настройки чатов
              </Link>
            </div>
          </div>
          <History points={g.history} />
        </div>
      </div>
    </Section>
  )
}

const groupName = (g: WorkChatGroup): string => g.direction_name ?? (g.chats[0]?.chat.title || g.key)

/** Вкладка направления: название, готовность по последнему анализу, отметка «работа», если робот уже работает хоть в одном чате. */
function GroupTab({ g }: { g: WorkChatGroup }) {
  const working = g.chats.some((c) => c.chat.mode === 'work')
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {working ? <span aria-hidden="true" className="h-1.5 w-1.5 shrink-0 rounded-full bg-ok" /> : null}
      <span>{groupName(g)}</span>
      {g.report ? (
        <span className={cx('tnum text-[12px] font-semibold', textColor[toneOf(g.report.readiness)])} title={`Готовность по последнему анализу: ${g.report.readiness}%`}>
          {g.report.readiness}%
        </span>
      ) : (
        <span className="text-[12px] text-subtle" title={g.report_pending ? 'Робот пишет первый анализ' : 'Анализа пока нет'}>
          {g.report_pending ? '…' : '—'}
        </span>
      )}
      {working ? <span className="sr-only">(робот в работе)</span> : null}
    </span>
  )
}

export function ChatsPage() {
  const list = useAsync((signal) => api.get<WorkChatGroup[]>('/api/work-chats/overview', { signal }), [])
  const [connecting, setConnecting] = useState(false)
  const reloadSoon = useDebounced(list.reload, 800)
  useStream((ev) => {
    if (ev.type === 'activity' && String(ev.data.action ?? '').startsWith('workchat.')) reloadSoon()
  })
  useReconnect(list.reload)
  const directions = useLive((s) => s.directions)
  useEffect(() => {
    if (!directions.length) void refreshCatalog()
  }, [directions.length])
  // направления — в порядке каталога (как в остальной OMA), чаты без направления — в конце
  const order = (g: WorkChatGroup): number => {
    const i = g.direction_id ? directions.findIndex((d) => d.id === g.direction_id) : -1
    return i < 0 ? 1000 : i
  }
  const groups = [...(list.data ?? [])].sort((a, b) => order(a) - order(b))
  // выбранное направление — в адресе (ссылкой можно поделиться), последнее выбранное запоминается
  const param = useSearchParam('d')
  const selected = groups.find((g) => g.key === param) ?? groups.find((g) => g.key === loadPref<string>('chats.dir', '')) ?? groups[0] ?? null
  const select = (key: string) => {
    setSearchParams({ d: key })
    savePref('chats.dir', key)
  }
  // на телефоне вкладки прокручиваются: выбранная должна быть видна
  const tabsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    tabsRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [selected?.key])
  return (
    <div className="page max-w-[1160px]">
      <PageHeader
        title="Чаты"
        lede="Направления, в рабочих чатах которых работает робот OMA. Выберите направление — по нему анализ робота по всем его чатам и оценка, насколько команда агентов готова работать сама."
        actions={
          <Button variant="primary" size="sm" icon="plus" onClick={() => setConnecting((v) => !v)}>
            Подключить направление
          </Button>
        }
      />
      {connecting ? (
        <div className="mb-5">
          <ConnectPanel
            onDone={(dir) => {
              list.reload()
              if (dir) select(dir)
            }}
            onClose={() => setConnecting(false)}
          />
        </div>
      ) : null}
      {list.error && !list.data ? (
        <ErrorBox error={list.error} onRetry={list.reload} />
      ) : !list.data ? (
        <Loading />
      ) : selected ? (
        <div className="flex flex-col gap-5">
          <div ref={tabsRef}>
            <Tabs<string> label="Направления с рабочими чатами" items={groups.map((g) => ({ id: g.key, label: <GroupTab g={g} /> }))} value={selected.key} onChange={select} />
          </div>
          <GroupCard key={selected.key} g={selected} onRequested={list.reload} />
        </div>
      ) : (
        <Empty icon="message">
          <div className="font-medium text-fg">Робот пока не подключён ни к одному чату</div>
          <div className="mt-1 text-[13px]">Нажмите «Подключить направление», выберите направление и его чаты — бот добавится в них сам.</div>
        </Empty>
      )}
    </div>
  )
}

/** Подключить направление (или добавить чат в направление): найти чаты по названию или ссылке, подключить, бот добавится сам. */
function ConnectPanel({ presetDirection, onDone, onClose }: { presetDirection?: string | null; onDone: (direction?: string) => void; onClose: () => void }) {
  const directions = useLive((s) => s.directions)
  useEffect(() => {
    if (!directions.length) void refreshCatalog()
  }, [directions.length])
  const [direction, setDirection] = useState(presetDirection ?? '')
  const [query, setQuery] = useState('')
  const q = useDebouncedValue(query.trim(), 400)
  const [found, setFound] = useState<ChatSearch | null>(null)
  const [searching, setSearching] = useState(false)
  const [picked, setPicked] = useState<ChatCandidate[]>([])
  const [results, setResults] = useState<ConnectResult[] | null>(null)
  const [busy, run] = useBusy()
  const ids = { direction: useId(), query: useId() }
  useEffect(() => {
    if (q.length < 2) {
      setSearching(false)
      setFound(null)
      return
    }
    const ctl = new AbortController()
    setSearching(true)
    api
      .get<ChatSearch>(`/api/work-chats/find?q=${encodeURIComponent(q)}`, { signal: ctl.signal })
      .then((r) => setFound(r ?? { items: [], link: false }))
      .catch((e) => {
        if (!ctl.signal.aborted) toastError(e, 'Поиск чатов не удался')
      })
      .finally(() => {
        if (!ctl.signal.aborted) setSearching(false)
      })
    return () => {
      ctl.abort()
      setSearching(false)
    }
  }, [q])
  const dirName = (id: string | null) => directions.find((d) => d.id === id)?.name ?? id ?? 'без направления'
  // список найденного не закрывается: можно выбрать несколько чатов подряд
  const pick = (c: ChatCandidate) => setPicked((cur) => (cur.some((x) => x.dialog_id === c.dialog_id) ? cur : [...cur, c]))
  const connect = () =>
    run(async () => {
      try {
        const r = await api.post<ConnectResult[]>('/api/work-chats/connect', { direction_id: direction, chats: picked.map((c) => ({ dialog_id: c.dialog_id, title: c.title })) })
        setResults(r ?? [])
        if ((r ?? []).every((x) => x.status === 'added' || x.status === 'already')) toast('Чаты подключены — робот начал читать', 'success')
        // подключённые убрать из выбора — повторно тот же набор не отправится
        setPicked((cur) => cur.filter((c) => (r ?? []).some((x) => x.dialog_id === c.dialog_id && x.status === 'error')))
        // подключили хоть один чат — страница переходит к этому направлению
        onDone((r ?? []).some((x) => x.status !== 'error') ? direction : undefined)
      } catch (e) {
        toastError(e, 'Не подключено')
      }
    })
  return (
    <Section
      title={presetDirection ? `Добавить чат в направление «${dirName(presetDirection)}»` : 'Подключить направление'}
      actions={
        <Button size="sm" variant="ghost" icon="x" onClick={onClose}>
          Закрыть
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {!presetDirection ? (
          <Field label="Направление" htmlFor={ids.direction}>
            <select id={ids.direction} className="ctl max-w-[360px]" value={direction} onChange={(e) => setDirection(e.target.value)}>
              <option value="">— выберите —</option>
              {directions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Чаты направления — название или ссылка на чат в Битрикс24" htmlFor={ids.query}>
          <div className="relative">
            <input
              id={ids.query}
              className="ctl"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="например: buying v. 2 — или вставьте ссылку на чат"
              autoComplete="off"
            />
            {searching ? <Icon name="loader" size={14} className="absolute top-1/2 right-3 -translate-y-1/2 animate-spin text-subtle" /> : null}
          </div>
        </Field>
        {found ? (
          found.items.length ? (
            <ul className="-mt-2 flex flex-col divide-y divide-line rounded-card border border-line">
              {found.items.map((c) => {
                const chosen = picked.some((x) => x.dialog_id === c.dialog_id)
                return (
                  <li key={c.dialog_id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[13px]">
                    <Icon name="message" size={14} className="shrink-0 text-subtle" />
                    <span className="min-w-0 font-medium [overflow-wrap:anywhere] text-fg">{c.title}</span>
                    <span className="font-mono text-[11.5px] text-subtle">{c.dialog_id}</span>
                    {c.last_at ? <span className="text-[12px] text-subtle">· последнее {formatDateTime(c.last_at)}</span> : null}
                    {c.connected ? (
                      c.connected.direction_id && c.connected.direction_id !== direction && direction ? (
                        // длинная подпись: на телефоне бейдж переносится строками, а не вылезает за край
                        <Badge
                          tone="warn"
                          className="max-w-full max-md:!h-auto max-md:py-0.5 max-md:!whitespace-normal max-md:leading-tight"
                          title="Чат перейдёт в выбранное направление: исполнитель и разборщик сменятся, режим — наблюдение"
                        >
                          подключён к «{dirName(c.connected.direction_id)}» — будет перенесён
                        </Badge>
                      ) : (
                        <Badge tone="accent" className="max-w-full max-md:!h-auto max-md:py-0.5 max-md:!whitespace-normal max-md:leading-tight">
                          уже подключён{c.connected.direction_id ? ` · ${dirName(c.connected.direction_id)}` : ''}
                        </Badge>
                      )
                    ) : null}
                    <span className="ml-auto" />
                    <Button size="xs" variant={chosen ? 'ghost' : 'secondary'} icon={chosen ? 'check' : 'plus'} disabled={chosen} onClick={() => pick(c)}>
                      {chosen ? 'Выбран' : 'Выбрать'}
                    </Button>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="-mt-2 text-[13px] text-subtle">
              {found.problem
                ? `Ссылка не подходит: ${found.problem}.`
                : found.link
                  ? 'Чат по ссылке не найден.'
                  : 'Чатов с таким названием среди ваших последних диалогов нет — вставьте ссылку на чат.'}
            </p>
          )
        ) : null}
        {picked.length ? (
          <div className="flex flex-wrap gap-2">
            {picked.map((c) => (
              <span key={c.dialog_id} className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-line bg-surface-2 py-1 pr-1.5 pl-3 text-[13px]">
                <span className="min-w-0 [overflow-wrap:anywhere]">{c.title}</span>
                <button type="button" aria-label={`Убрать ${c.title}`} className="shrink-0 rounded-full p-0.5 text-subtle hover:bg-surface-3 hover:text-fg" onClick={() => setPicked((cur) => cur.filter((x) => x.dialog_id !== c.dialog_id))}>
                  <Icon name="x" size={12} />
                </button>
              </span>
            ))}
          </div>
        ) : null}
        <p className="text-[12.5px] text-muted">
          Новые чаты робот начнёт в режиме наблюдения (уже подключённые к этому направлению остаются в своём режиме): читает и разбирает, в чат не пишет и задач не ставит. Бот OMA добавится в чаты от вашего имени — в чате появится строка «… пригласил OMA», больше ничего. Через 10 минут робот напишет первый анализ направления и черновик правил для новых чатов — проверьте его в Настройках → «Рабочие чаты».
        </p>
        <div>
          <Button variant="primary" size="sm" loading={busy} disabled={!direction || !picked.length} onClick={() => void connect()}>
            Подключить и добавить бота
          </Button>
        </div>
        {results ? (
          <ul className="flex flex-col gap-1 text-[13px]">
            {results.map((r) => (
              <li key={r.dialog_id} className="flex flex-wrap items-center gap-2">
                <Icon name={r.status === 'error' || r.status === 'no_bot' ? 'alert' : 'check'} size={14} className={cx('shrink-0', r.status === 'error' ? 'text-danger' : r.status === 'no_bot' ? 'text-warn' : 'text-ok')} />
                <span className="min-w-0 font-medium [overflow-wrap:anywhere]">{r.title}</span>
                <span className="min-w-0 [overflow-wrap:anywhere] text-muted">
                  {r.status === 'added'
                    ? '— подключён, бот добавлен'
                    : r.status === 'already'
                      ? '— подключён, бот уже был в чате'
                      : r.status === 'no_bot'
                        ? `— подключён и читается в наблюдении, но бота добавить не удалось (${r.error}). Добавьте бота OMA в чат вручную`
                        : `— не подключён: ${r.error}`}
                  {r.moved_from ? ` · перенесён из «${dirName(r.moved_from)}»` : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Section>
  )
}
